// Iteration 48 — Technician Guide button + modal.
import React, { useState } from "react";
import { Loader2, Wrench, X, AlertTriangle, CheckCircle2, Info, ThumbsUp, ThumbsDown, Download, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";

export function TechGuideButton({ jobId, opKey, opName, calType, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10 ${className}`}
        data-testid={`tech-guide-btn-${opKey}`}
      >
        <Wrench className="w-3 h-3" /> Tech Guide
      </button>
      {open && (
        <TechGuideModal
          jobId={jobId}
          opKey={opKey}
          opName={opName}
          calType={calType}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function TechGuideModal({ jobId, opKey, opName, calType, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setBusy(true);
    api
      .post(`/tech-guide/for-operation`, { job_id: jobId, op_key: opKey })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => !cancelled && setErr(e?.response?.data?.detail || "Failed to load"))
      .finally(() => !cancelled && setBusy(false));
    return () => { cancelled = true; };
  }, [jobId, opKey]);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-6 overflow-y-auto" onClick={onClose}>
      <div
        className="w-full max-w-3xl bg-[#0B1120] border border-cyan-500/20 rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="tech-guide-modal"
      >
        <div className="border-b border-slate-800 p-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-cyan-400 font-bold">Technician Guide</div>
            <div className="font-display text-xl">{opName}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {calType && <Badge className="bg-slate-800 border-slate-700 text-[9px] mr-1.5 uppercase">{calType}</Badge>}
              op_key: <span className="font-mono">{opKey}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200" data-testid="tech-guide-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {busy && (
          <div className="p-8 text-center text-slate-400 text-sm">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-cyan-400" />
            AI is preparing your calibration guide (10-30s)...
          </div>
        )}
        {err && !busy && (
          <div className="p-6 text-center space-y-2" data-testid="tech-guide-error">
            <div className="text-rose-300 text-sm">
              {typeof err === "string" ? err : "Failed to load guide"}
            </div>
            <div className="text-[11px] text-slate-500">
              If this keeps happening, make sure the job is saved and that the AI service is reachable.
              You can also try the ADAS Analysis page to generate a guide standalone.
            </div>
          </div>
        )}
        {data && !busy && (
          <>
            <GuideBody guide={data.guide} isAdas={data.is_adas} vehicle={data.vehicle_label} />
            <FeedbackBar cacheKey={data.cache_key} jobId={jobId} opKey={opKey} />
          </>
        )}
      </div>
    </div>
  );
}

function FeedbackBar({ cacheKey, jobId, opKey }) {
  const [rating, setRating] = useState(null); // "up" | "down" | null
  const [comment, setComment] = useState("");
  const [showComment, setShowComment] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [downloading, setDownloading] = useState(false);

  React.useEffect(() => {
    if (!cacheKey) return;
    api.get(`/tech-guide/feedback/mine`, { params: { cache_key: cacheKey } })
      .then((r) => {
        if (r.data?.rating) {
          setRating(r.data.rating);
          setSubmitted(true);
          setComment(r.data.comment || "");
        }
      })
      .catch(() => {});
  }, [cacheKey]);

  const submit = async (r, note) => {
    if (!cacheKey) return;
    setSubmitting(true);
    try {
      await api.post(`/tech-guide/feedback`, {
        cache_key: cacheKey, rating: r, comment: note || null, job_id: jobId,
      });
      setRating(r);
      setSubmitted(true);
      setShowComment(false);
      toast.success("Thanks for the feedback");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Feedback failed");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const resp = await api.post(
        `/tech-guide/pdf/for-operation`,
        { job_id: jobId, op_key: opKey },
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = resp.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(cd);
      a.download = match ? match[1] : `TechGuide-${opKey}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("PDF export failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="border-t border-slate-800 p-4 space-y-3 bg-slate-950/60 rounded-b-lg">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-500">Was this helpful?</span>
          <button
            type="button"
            disabled={submitting}
            onClick={() => { if (rating !== "up") submit("up"); }}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded border text-xs transition ${
              rating === "up"
                ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-200"
                : "border-slate-700 text-slate-400 hover:border-emerald-500/50 hover:text-emerald-300"
            }`}
            data-testid="tech-guide-thumb-up"
          >
            <ThumbsUp className="w-3.5 h-3.5" /> Yes
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={() => { setShowComment(true); if (rating !== "down") setRating("down"); }}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded border text-xs transition ${
              rating === "down"
                ? "bg-rose-500/20 border-rose-500/50 text-rose-200"
                : "border-slate-700 text-slate-400 hover:border-rose-500/50 hover:text-rose-300"
            }`}
            data-testid="tech-guide-thumb-down"
          >
            <ThumbsDown className="w-3.5 h-3.5" /> No
          </button>
          {submitted && !showComment && (
            <button
              type="button"
              onClick={() => setShowComment(true)}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-300"
              data-testid="tech-guide-add-comment"
            >
              <MessageSquare className="w-3 h-3" /> Add note
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={downloadPdf}
          disabled={downloading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/10 text-xs font-medium disabled:opacity-60"
          data-testid="tech-guide-download-pdf"
        >
          {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          {downloading ? "Preparing…" : "Download PDF"}
        </button>
      </div>

      {showComment && (
        <div className="space-y-2">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, 1000))}
            placeholder="What was missing or wrong? (optional, helps improve future guides)"
            rows={2}
            className="w-full bg-slate-900 border border-slate-800 rounded p-2 text-xs text-slate-200 focus:border-cyan-500/50 outline-none"
            data-testid="tech-guide-comment-input"
          />
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => { setShowComment(false); }}
              className="text-[11px] text-slate-500 hover:text-slate-300 px-2 py-1"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={submitting || !rating}
              onClick={() => submit(rating, comment)}
              className="text-[11px] uppercase tracking-wider px-3 py-1.5 rounded bg-cyan-600 hover:bg-cyan-500 text-white disabled:opacity-60"
              data-testid="tech-guide-comment-submit"
            >
              {submitting ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function GuideBody({ guide, isAdas, vehicle }) {
  const g = guide || {};
  return (
    <div className="p-5 space-y-4 text-sm">
      {vehicle && <div className="text-[11px] text-slate-500">Vehicle: <span className="text-slate-300">{vehicle}</span></div>}
      {g.summary && (
        <div className="rounded-md bg-cyan-500/5 border border-cyan-500/20 p-3 text-slate-200 italic" data-testid="guide-summary">
          {g.summary}
        </div>
      )}

      {isAdas && g.calibration_type && (
        <Section title="Calibration Type" icon={CheckCircle2} accent="emerald">
          <Badge className="bg-emerald-500/20 border-emerald-500/40 text-emerald-200 uppercase">
            {g.calibration_type}
          </Badge>
          {g.method_short && <span className="ml-2 text-slate-300">{g.method_short}</span>}
        </Section>
      )}

      {g.tools_needed?.length > 0 && (
        <Section title="Tools Needed" icon={Wrench} accent="cyan">
          <ul className="space-y-1">
            {g.tools_needed.map((t, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-cyan-400 mt-0.5">•</span>
                <span className="text-slate-200">
                  <span className="font-semibold">{t.name || t}</span>
                  {t.purpose && <span className="text-slate-400"> — {t.purpose}</span>}
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {g.pre_conditions?.length > 0 && (
        <Section title="Pre-Conditions" icon={Info} accent="amber">
          <ul className="space-y-1 list-disc list-inside text-slate-300">
            {g.pre_conditions.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </Section>
      )}

      {g.procedure_steps?.length > 0 && (
        <Section title="Procedure Steps" icon={CheckCircle2} accent="blue">
          <ol className="space-y-2">
            {g.procedure_steps.map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="flex-none w-6 h-6 rounded-full bg-blue-500/20 border border-blue-500/40 text-blue-200 text-xs font-bold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <div className="text-slate-200 flex-1">
                  {typeof s === "string" ? s : (
                    <>
                      <div>{s.step}</div>
                      {s.note && <div className="text-[11px] text-slate-500 mt-0.5 italic">{s.note}</div>}
                    </>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {g.safety_warnings?.length > 0 && (
        <Section title="Safety Warnings" icon={AlertTriangle} accent="rose">
          <ul className="space-y-1 list-disc list-inside text-rose-200">
            {g.safety_warnings.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </Section>
      )}

      {g.verification?.length > 0 && (
        <Section title="Verification / Post-Scan Checks" icon={CheckCircle2} accent="emerald">
          <ul className="space-y-1 list-disc list-inside text-slate-300">
            {g.verification.map((v, i) => <li key={i}>{v}</li>)}
          </ul>
        </Section>
      )}

      {g.oem_sources?.length > 0 && (
        <Section title="OEM Sources" icon={Info} accent="slate">
          <ul className="space-y-1 text-[11px] text-slate-400">
            {g.oem_sources.map((o, i) => <li key={i}>· {o}</li>)}
          </ul>
        </Section>
      )}

      <div className="text-[10px] text-slate-600 italic pt-2 border-t border-slate-800">
        ⚠ AI-generated guidance. Always verify against the OEM procedure before performing the calibration.
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, accent = "slate", children }) {
  const colors = {
    cyan: "text-cyan-300 border-cyan-500/20 bg-cyan-500/5",
    emerald: "text-emerald-300 border-emerald-500/20 bg-emerald-500/5",
    amber: "text-amber-300 border-amber-500/20 bg-amber-500/5",
    rose: "text-rose-300 border-rose-500/20 bg-rose-500/5",
    blue: "text-blue-300 border-blue-500/20 bg-blue-500/5",
    slate: "text-slate-300 border-slate-500/20 bg-slate-500/5",
  }[accent] || colors?.slate;
  return (
    <div className={`rounded-md border p-3 ${colors}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold mb-2">
        <Icon className="w-3 h-3" /> {title}
      </div>
      {children}
    </div>
  );
}

// DTC helper — smaller inline component for non-ADAS findings
export function DtcHelpButton({ jobId, dtcCode, module, vehicleLabel }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [cacheKey, setCacheKey] = useState(null);
  const [rating, setRating] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setBusy(true);
    api
      .post(`/tech-guide/for-dtc`, { job_id: jobId, dtc_code: dtcCode, module, vehicle_label: vehicleLabel })
      .then((r) => {
        setData(r.data.guide || {});
        setCacheKey(r.data.cache_key || null);
      })
      .catch(() => setData({}))
      .finally(() => setBusy(false));
  };

  const rate = async (r) => {
    if (!cacheKey || rating === r) return;
    try {
      await api.post(`/tech-guide/feedback`, { cache_key: cacheKey, rating: r, job_id: jobId });
      setRating(r);
      toast.success("Thanks");
    } catch { /* silent */ }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => { setOpen(true); load(); }}
        className="text-[10px] uppercase tracking-wider text-amber-300 hover:text-amber-200 flex items-center gap-1"
        data-testid={`dtc-help-btn-${dtcCode}`}
      >
        <Wrench className="w-3 h-3" /> Diagnostic help
      </button>
    );
  }
  return (
    <div className="rounded-md bg-slate-900/60 border border-amber-500/30 p-2 space-y-1.5 mt-1">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider font-bold text-amber-300 flex items-center gap-1">
          <Wrench className="w-3 h-3" /> DTC Guide · {dtcCode}
        </div>
        <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-300 text-[10px]">✕</button>
      </div>
      {busy || !data ? (
        <div className="text-[11px] text-slate-400 italic">Loading…</div>
      ) : (
        <div className="space-y-1.5 text-[11px]">
          {data.likely_causes?.length > 0 && <MiniList label="Likely causes" items={data.likely_causes} color="slate" />}
          {data.diagnostic_hints?.length > 0 && <MiniList label="Diagnostic steps" items={data.diagnostic_hints} ordered color="slate" />}
          {data.escalate_if?.length > 0 && <MiniList label="Escalate if" items={data.escalate_if} color="amber" />}
          {data.safety_flags?.length > 0 && <MiniList label="Safety" items={data.safety_flags} color="rose" />}
          {cacheKey && (
            <div className="flex items-center gap-2 pt-1 border-t border-slate-800/60">
              <span className="text-[9px] uppercase text-slate-500">Helpful?</span>
              <button
                type="button"
                onClick={() => rate("up")}
                className={`p-0.5 rounded ${rating === "up" ? "text-emerald-300" : "text-slate-500 hover:text-emerald-300"}`}
                data-testid={`dtc-thumb-up-${dtcCode}`}
              >
                <ThumbsUp className="w-3 h-3" />
              </button>
              <button
                type="button"
                onClick={() => rate("down")}
                className={`p-0.5 rounded ${rating === "down" ? "text-rose-300" : "text-slate-500 hover:text-rose-300"}`}
                data-testid={`dtc-thumb-down-${dtcCode}`}
              >
                <ThumbsDown className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MiniList({ label, items, ordered = false, color = "slate" }) {
  const cls = { slate: "text-slate-300", amber: "text-amber-300", rose: "text-rose-300" }[color];
  const Tag = ordered ? "ol" : "ul";
  return (
    <div>
      <div className="text-[9px] uppercase text-slate-500 mb-0.5">{label}</div>
      <Tag className={`${ordered ? "list-decimal" : "list-disc"} list-inside space-y-0.5 ${cls}`}>
        {items.map((s, i) => <li key={i}>{s}</li>)}
      </Tag>
    </div>
  );
}
